// General enums for the application

// Vehicle type enum with numeric values for backend
export enum VehicleType {
  BUS = 4,
  CAR = 2,
  MICROBUS = 6,
  MINIBUS = 5,
  MOTORCYCLE = 1,
  OTHER = 8,
  TRAILER = 7,
  TRUCK = 3
}

// Vehicle type options for form components
export const VEHICLE_TYPE_OPTIONS = [
  { label: 'Motorcycle', value: VehicleType.MOTORCYCLE.toString() },
  { label: 'Car', value: VehicleType.CAR.toString() },
  { label: 'Truck', value: VehicleType.TRUCK.toString() },
  { label: 'Bus', value: VehicleType.BUS.toString() },
  { label: 'Minibus', value: VehicleType.MINIBUS.toString() },
  { label: 'Microbus', value: VehicleType.MICROBUS.toString() },
  { label: 'Trailer', value: VehicleType.TRAILER.toString() },
  { label: 'Other', value: VehicleType.OTHER.toString() }
]

// License class (driver license type) enum with numeric values for backend
// Values 1-4 are legacy ("OLD") classes, 5-16 are new typed classes
export enum LicenseClass {
  A = 5,
  A1 = 6,
  B = 7,
  B1 = 8,
  BE = 13,
  C = 9,
  C1 = 10,
  C1E = 14,
  CE = 15,
  D = 11,
  D1 = 12,
  DE = 16,
  OLD_FIRST = 1,
  OLD_PRIVATE = 4,
  OLD_SECOND = 2,
  OLD_THIRD = 3
}

/**
 * License class type code for display labels.
 * Format: `{type} ({description})` e.g. "B1 (Three-wheeled vehicles)"
 */
export const LICENSE_CLASS_TYPE_CODE: Record<number, string> = {
  [LicenseClass.A]: 'A',
  [LicenseClass.A1]: 'A1',
  [LicenseClass.B]: 'B',
  [LicenseClass.B1]: 'B1',
  [LicenseClass.BE]: 'BE',
  [LicenseClass.C]: 'C',
  [LicenseClass.C1]: 'C1',
  [LicenseClass.C1E]: 'C1E',
  [LicenseClass.CE]: 'CE',
  [LicenseClass.D]: 'D',
  [LicenseClass.D1]: 'D1',
  [LicenseClass.DE]: 'DE',
  [LicenseClass.OLD_FIRST]: 'OLD',
  [LicenseClass.OLD_PRIVATE]: 'OLD',
  [LicenseClass.OLD_SECOND]: 'OLD',
  [LicenseClass.OLD_THIRD]: 'OLD'
}

// License class options for form components.
// Labels mirror backend EN labels; UI display uses `enums.license_class.<id>` via next-intl.
export const LICENSE_CLASS_OPTIONS = Object.values(LicenseClass)
  .filter((v): v is number => typeof v === 'number')
  .map((v) => ({
    label: `${LICENSE_CLASS_TYPE_CODE[v]} (${v})`, // fallback; real label from i18n
    value: v.toString()
  }))

// Gender enum with numeric values for backend
export enum Gender {
  FEMALE = 2,
  MALE = 1
}

// Gender options for form components
export const GENDER_OPTIONS = [
  { label: 'Male', value: Gender.MALE.toString() },
  { label: 'Female', value: Gender.FEMALE.toString() }
]

// Fuel type enum with numeric values for backend
export enum FuelType {
  DIESEL = '2',
  ELECTRIC = '3',
  HYBRID = '4',
  HYDROGEN = '6',
  LPG = '5', // Gas
  PETROL = '1' // Benzin
}

// Fuel type options for form components
export const FUEL_TYPE_OPTIONS = [
  { label: 'Petrol (Benzin)', value: FuelType.PETROL },
  { label: 'Diesel', value: FuelType.DIESEL },
  { label: 'Electric', value: FuelType.ELECTRIC },
  { label: 'Hybrid', value: FuelType.HYBRID },
  { label: 'LPG (Gas)', value: FuelType.LPG },
  { label: 'Hydrogen', value: FuelType.HYDROGEN }
]

export enum Blocked {
  BLOCKED = 1,
  UNBLOCKED = 0
}

// Status enum with string values for form validation
export enum StatusEnum {
  ACTIVE = 1,
  INACTIVE = 0
}

// Status options for form components
export const STATUS_OPTIONS = [
  { label: 'Inactive', value: StatusEnum.INACTIVE },
  { label: 'Active', value: StatusEnum.ACTIVE }
]

// Gearbox/Transmission enum with numeric values for backend
export enum Gearbox {
  AUTOMATIC = 2,
  CVT = 4,
  DUAL_CLUTCH = 5,
  MANUAL = 1,
  SEMI_AUTOMATIC = 3
}

export enum ServiceCenterTypeEnum {
  BOTH = 'both',
  CRITICAL = 'critical',
  PERIODIC = 'periodic'
}

export enum ServiceCenterTypeNumbersEnum {
  BOTH = 3,
  CRITICAL = 2,
  PERIODIC = 1
}

// Minimal gearbox options requested for UI (Automatic/Manual only)
export const GEARBOX_OPTIONS = [
  { label: 'Manual', value: Gearbox.MANUAL },
  { label: 'Automatic', value: Gearbox.AUTOMATIC }
]

export enum AttachmentTypeEnum {
  IMAGE = 1,
  PDF = 2
}

export enum ContractTypeEnum {
  CORPORATE = 1,
  SCHOOL = 2
}

/**
 * `section` on each row in the admin contract create/update `vehicle_types` payload.
 * 1 Transportation, 2 Dynamic, 3 Special (additional/special flows).
 */
export enum ContractVehicleTypeSectionEnum {
  DYNAMIC = 2,
  SPECIAL = 3,
  TRANSPORTATION = 1
}

export enum DaysOfWeekEnum {
  FRIDAY = 7,
  MONDAY = 3,
  SATURDAY = 1,
  SUNDAY = 2,
  THURSDAY = 6,
  TUESDAY = 4,
  WEDNESDAY = 5
}

export enum MaintenanceRouteStatusEnum {
  APPROVED = 1,
  CANCELLED = 5,
  COMPLETED = 4,
  IN_PROGRESS = 3,
  PENDING = 0,
  REJECTED = 2
}

export enum PaymentTermsEnum {
  HALF_YEARLY = 3,
  MONTHLY = 1,
  QUARTERLY = 2,
  YEARLY = 4
}

// export enum PaymentTermEnum {
//   HALF_YEARLY = 3,
//   MONTHLY = 1,
//   QUARTERLY = 2,
//   YEARLY = 4
// }

/**
 * Payslip attachment matching status (`/payslip-attachments`).
 * TODO: confirm exact values/labels with backend — inferred from the sample response where an
 * unmatched attachment (`is_linked: false`, `payslip: null`) came back with `status: 4`.
 */
export enum PayslipAttachmentStatusEnum {
  INVALID_NAME = 2,
  MATCHED = 3,
  UNMATCHED = 4,
  UPLOADED = 1
}

/**
 * `point_change_policy` of a Restricted Fixed Destination (Model 4) contract.
 * Set on the contract only; the backend copies it read-only to routes, shifts and trips.
 */
export enum PointChangePolicyEnum {
  ALLOW_POINT_CHANGES = 2,
  DEFAULT_POINTS_ONLY = 1
}

/** Tenant contract `contract_type` (admin API) — distinct from `TripTypeEnum`. */
export enum TenantContractTypeEnum {
  DYNAMIC_ROUTE = 3,
  FIXED_DESTINATION = 2,
  FIXED_ROUTE = 1,
  RESTRICTED_FIXED_DESTINATION = 4
}

/** Routing mode for trip request create payload. */
export enum TripRequestRoutingModeEnum {
  FULL_DAY = 3,
  SEVERAL_POINTS = 2,
  SINGLE_DESTINATION = 1
}

/** Vehicle models endpoint request `type` (admin API). */
export enum TripRequestTypeEnum {
  DYNAMIC = 2,
  ROUTE = 1,
  SPECIAL = 3
}

export const PAYMENT_TERMS_OPTIONS = [
  { label: 'Monthly', value: PaymentTermsEnum.MONTHLY.toString() },
  { label: 'Quarterly', value: PaymentTermsEnum.QUARTERLY.toString() },
  { label: 'Half-yearly', value: PaymentTermsEnum.HALF_YEARLY.toString() },
  { label: 'Yearly', value: PaymentTermsEnum.YEARLY.toString() }
]

export const CONTRACT_TYPE_OPTIONS = [
  { label: 'Corporate', value: ContractTypeEnum.CORPORATE.toString() },
  { label: 'School', value: ContractTypeEnum.SCHOOL.toString() }
]

export enum RequestTypeEnum {
  DYNAMIC_REQUEST = 2,
  SPECIAL_REQUEST = 1
}

export enum RerouteRequestStatusEnum {
  APPROVED = 2,
  CANCELLED = 4,
  PENDING = 1,
  REJECTED = 3
}
export enum ResignationRequestStatusEnum {
  APPROVED = 2,
  PENDING = 1,
  REJECTED = 3
}

export enum ResignationRequestTypeEnum {
  END_OF_CONTRACT = 3,
  RETIREMENT = 2,
  VOLUNTARY = 1
}

export enum RiderTypeEnum {
  EMPLOYEE = 1,
  PARENT = 3,
  STUDENT = 2
}

export enum RouteDirectionEnum {
  DEPARTURE = 1,
  RETURN = 2
}

/** Route shift stop `has_reservations` flag from GET /route-shifts/{id}. */
export enum RouteShiftStopHasReservationsEnum {
  NO = 0,
  YES = 1
}

/** Tenant route `type`; always equals the contract's `contract_type` (there is no route type 3). */
export enum RouteTypeEnum {
  FIXED_DESTINATION = 2,
  FIXED_ROUTE = 1,
  RESTRICTED_FIXED_DESTINATION = 4
}

export enum ScheduleTypeEnum {
  PERIOD = 1,
  ROUTINE = 2
}

export enum ServiceRouteStatusEnum {
  CANCELLED = 3,
  COMPLETED = 2,
  IN_PROGRESS = 1
}

export enum ServiceRouteTypeEnum {
  ADMINISTRATIVE = 4,
  CARWASH = 2,
  FUEL = 1,
  OFFICE = 3
}

export enum TenantType {
  CORPORATE = '1',
  SCHOOL = '2'
}

export enum TenantTypeEnum {
  CORPORATE = 1,
  SCHOOL = 2
}

export enum TripRequestCareTypeEnum {
  BUS = 4,
  CAR = 1,
  MICROBUS = 2,
  MINIBUS = 3
}

export enum TripStatusEnum {
  CANCELLED = 4,
  COMPLETED = 3,
  IN_PROGRESS = 2,
  SCHEDULED = 1
}

export enum TripStopStatusEnum {
  ARRIVED = 2,
  COMPLETED = 3,
  PENDING = 1,
  SKIPPED = 4
}

/** Route type 4 (Restricted Fixed Destination) generates trip type 5, never 4. */
export enum TripTypeEnum {
  DYNAMIC_REQUEST = 3,
  FIXED_DESTINATION = 2,
  FIXED_ROUTE = 1,
  RESTRICTED_FIXED_DESTINATION = 5,
  SPECIAL_REQUEST = 4
}

export enum VehicleBodyTypeEnum {
  BUS = 6,
  HATCHBACK = 2,
  MICROBUS = 8,
  MINIBUS = 7,
  MINIVAN = 5,
  OTHER = 9,
  SEDAN = 1,
  SUV = 3,
  VAN = 4
}

export const TRIP_REQUEST_CARE_TYPE_OPTIONS = [
  { label: 'Car', value: TripRequestCareTypeEnum.CAR.toString() },
  { label: 'Microbus', value: TripRequestCareTypeEnum.MICROBUS.toString() },
  { label: 'Minibus', value: TripRequestCareTypeEnum.MINIBUS.toString() },
  { label: 'Bus', value: TripRequestCareTypeEnum.BUS.toString() }
]

// Vehicle body type options for display
export const VEHICLE_BODY_TYPE_OPTIONS = [
  { label: 'Sedan', value: VehicleBodyTypeEnum.SEDAN.toString() },
  { label: 'Hatchback', value: VehicleBodyTypeEnum.HATCHBACK.toString() },
  { label: 'SUV', value: VehicleBodyTypeEnum.SUV.toString() },
  { label: 'Van', value: VehicleBodyTypeEnum.VAN.toString() },
  { label: 'Minivan', value: VehicleBodyTypeEnum.MINIVAN.toString() },
  { label: 'Bus', value: VehicleBodyTypeEnum.BUS.toString() },
  { label: 'Minibus', value: VehicleBodyTypeEnum.MINIBUS.toString() },
  { label: 'Microbus', value: VehicleBodyTypeEnum.MICROBUS.toString() },
  { label: 'Other', value: VehicleBodyTypeEnum.OTHER.toString() }
]

export const MaintenanceRouteRejectReason = {
  BUDGET_CONSTRAINT: 4, // Budget constraints prevent approval
  DUPLICATE_REQUEST: 3, // Request is a duplicate
  LACK_OF_INFORMATION: 2, // Insufficient information provided
  OTHER: 6, // Other reasons
  OUT_OF_SCOPE: 1, // Request is outside maintenance scope
  SCHEDULE_CONFLICT: 5 // Schedule conflicts exist
} as const

export enum EmergencyPriorityEnum {
  CRITICAL = 4,
  HIGH = 3,
  LOW = 1,
  MEDIUM = 2
}

// Emergency Module
export enum EmergencyStatusEnum {
  ACKNOWLEDGED = 2, // Emergency has been acknowledged
  IN_PROGRESS = 3, // Emergency is currently being handled
  OPEN = 1, // Emergency is open/pending
  RESOLVED = 4 // Emergency has been resolved
}

export enum EmergencyTypeEnum {
  MEDICAL = 1, // Medical emergency
  OTHER = 3, // Neither medical nor vehicle
  VEHICLE = 2 // Vehicle-related emergency
}

export enum MaintenanceRouteRejectReasonEnum {
  BUDGET_CONSTRAINT = 4, // Budget constraints prevent approval
  DUPLICATE_REQUEST = 3, // Request is a duplicate
  LACK_OF_INFORMATION = 2, // Insufficient information provided
  OTHER = 6, // Other reasons
  OUT_OF_SCOPE = 1, // Request is outside maintenance scope
  SCHEDULE_CONFLICT = 5 // Schedule conflicts exist
}

export enum NotificationActionTypeEnum {
  CAR_RENTAL = 10,
  DYNAMIC_ROUTE = 5,
  EMERGENCY = 8,
  LEAVE_REQUEST = 2,
  MAINTENANCE = 9,
  PAYSLIP = 4,
  RESIGNATION = 3,
  SPECIAL_REQUEST = 6,
  TRIP = 1
}

/**
 * Delivery channels accepted by the notification send endpoints.
 */
export enum NotificationChannelEnum {
  DATABASE = 'database',
  EMAIL = 'email',
  FCM = 'fcm',
  SMS = 'sms'
}

/**
 * Audience scope for POST notifications/send-scoped-notification.
 * Scope IDs are the ids of the scope entity itself (e.g. TRIP -> trip ids).
 * ALL takes no scope IDs; every other scope requires at least one.
 */
export enum NotificationRecipientScopeEnum {
  ALL = 1,
  ROUTE = 3,
  SHIFT = 4,
  TENANT = 2,
  TRIP = 5
}

export enum NotificationStatus {
  FAILED = 'failed',
  PENDING = 'pending',
  SENT = 'sent'
}

export enum NotificationTargetEnum {
  ALL_ADMINS = 7,
  ALL_DRIVERS = 2,
  ALL_RIDERS = 1,
  ALL_ROUTE_DRIVERS = 10,
  ALL_ROUTE_RIDERS = 14,
  ALL_SHIFT_DRIVERS = 11,
  ALL_SHIFT_RIDERS = 15,
  ALL_TENANT_ADMINS = 3,
  ALL_TENANT_DRIVERS = 9,
  ALL_TENANT_RIDERS = 13,
  SHIFT_DRIVER = 17,
  SPECIFIC_ADMINS = 8,
  SPECIFIC_DRIVERS = 5,
  SPECIFIC_RIDERS = 4,
  SPECIFIC_TENANT_ADMINS = 6,
  SPECIFIC_TRIP_DRIVERS = 12,
  SPECIFIC_TRIP_RIDERS = 16
}

/**
 * Driver license allowed vehicles (plate_type).
 * Values 1-5 are the new fleet-specific types (backend migration 2026-06).
 */
export enum PlateTypeEnum {
  BUS_50_SEAT = 3,
  COASTER = 2,
  HIACE = 1,
  PRIVATE_VEHICLES = 4,
  TRUCKS = 5
}

export enum SupportTypeEnum {
  DRIVER = 3,
  MECHANIC = 1,
  MEDICAL = 2, // Medical support type
  OTHER = 4
}

/** Support ticket status (driver/rider/admin my-tickets). */
export enum TicketStatusEnum {
  REJECTED = 4,
  RESOLVED = 3,
  REVIEWING = 2,
  SUBMITTED = 1
}

/**
 * Chip variants for each ticket status.
 * Adding a new `TicketStatusEnum` member requires a matching entry here.
 */
export const TICKET_STATUS_CHIP_VARIANT = {
  [TicketStatusEnum.REJECTED]: 'destructive',
  [TicketStatusEnum.RESOLVED]: 'success',
  [TicketStatusEnum.REVIEWING]: 'info',
  [TicketStatusEnum.SUBMITTED]: 'warning'
} as const satisfies Record<TicketStatusEnum, string>

/** Car rental contract `status` (Rental module). Derived from its rentals. */
export enum CarRentalContractStatusEnum {
  CANCELLED = 3,
  COMPLETED = 2,
  IN_PROGRESS = 1,
  NOT_STARTED = 0
}

/** Odometer slot `status` inside a car rental. */
export enum CarRentalSlotStatusEnum {
  COMPLETED = 2,
  IN_PROGRESS = 1,
  PENDING = 0
}

/** Car rental `status`. Derived from its slots. */
export enum CarRentalStatusEnum {
  CANCELLED = 3,
  COMPLETED = 2,
  IN_PROGRESS = 1,
  NOT_STARTED = 0
}

export enum CarRentalTypeEnum {
  WITH_DRIVER = 1,
  WITHOUT_DRIVER = 2
}

/** Mileage history `source_type` (GET /vehicles/{vehicleId}/mileage-histories). */
export enum MileageHistorySourceTypeEnum {
  CAR_RENTAL_SLOT_END = 6,
  CAR_RENTAL_SLOT_START = 5,
  MAINTENANCE_ROUTE = 2,
  MANUAL = 4,
  SERVICE_ROUTE = 3,
  TRIP = 1
}

/** How often a car rental's odometer is reported; one slot per period. */
export enum OdometerReportingFrequencyEnum {
  DAILY = 2,
  MONTHLY = 4,
  ONE_TIME = 1,
  QUARTERLY = 5,
  SEMI_ANNUAL = 6,
  WEEKLY = 3,
  YEARLY = 7
}

export enum TripPassengerStatusEnum {
  ABSENT = 3,
  ATTENDED = 4,
  BOOKED = 1,
  CANCELLED = 2,
  NEED_CONFIRMATION_TO_BE_BOOKED = 6,
  SKIPPED = 8,
  WAITING_FOR_ADMIN_APPROVAL = 7,
  WAITING_FOR_CONFIRMATION = 5
}

export enum UserTypeEnum {
  DRIVER = 2,
  RIDER = 1,
  TENANT_ADMIN = 3
}

export type TicketStatusChipVariant =
  (typeof TICKET_STATUS_CHIP_VARIANT)[TicketStatusEnum]
